import React, { useEffect, useState, useCallback } from 'react';
import {
  Box, Button, Card, CardContent, Chip, Dialog, DialogActions, DialogContent, DialogTitle,
  IconButton, MenuItem, Pagination, Stack, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Typography, Tooltip, Alert
} from '@mui/material';
import { Add, Delete, Edit, PersonAdd, Upload } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import type { Prospect } from '../types';
import TextField from '../components/common/TextField';

const STATUS_OPTIONS = ['Activo', 'Inactivo', 'En seguimiento', 'Pendiente', 'Descartado'];
const FOLLOWUP_OPTIONS = ['En seguimiento', 'Pendiente', 'Completado', 'No contactado'];

const STATUS_COLORS: Record<string, string> = {
  'Activo': '#4CAF50',
  'En seguimiento': '#03A9F4',
  'Pendiente': '#FFC107',
  'Inactivo': '#9E9E9E',
  'Descartado': '#F44336',
};

export const ProspectsPage: React.FC = () => {
  const navigate = useNavigate();
  const [items, setItems] = useState<Prospect[]>([]);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(50);
  const [totalCount, setTotalCount] = useState(0);
  const totalPages = Math.ceil(totalCount / pageSize);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [sellerFilter, setSellerFilter] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [openForm, setOpenForm] = useState(false);
  const [editing, setEditing] = useState<Prospect | null>(null);
  const [form, setForm] = useState<any>({
    name: '', legalName: '', buyerName: '', generalStatus: 'Activo', followUpStatus: '',
    nextStep: '', phone: '', email: '', city: '', websiteUrl: '', sector: '',
    assignedSeller: '', branchCount: 0, notes: ''
  });

  const load = useCallback(async (p = page) => {
    setLoading(true); setError(null);
    try {
      const res = await api.getProspects({ page: p, pageSize, search, status: statusFilter, seller: sellerFilter });
      setItems(res.items || []);
      setTotalCount(res.totalCount || 0);
    } catch (e: any) {
      setError(e?.response?.data?.message || 'Error al cargar prospectos');
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, search, statusFilter, sellerFilter]);

  useEffect(() => { load(1); }, []);

  const handleSearch = () => { setPage(1); load(1); };

  const openNew = () => {
    setEditing(null);
    setForm({
      name: '', legalName: '', buyerName: '', generalStatus: 'Activo', followUpStatus: '',
      nextStep: '', phone: '', email: '', city: '', websiteUrl: '', sector: '',
      assignedSeller: '', branchCount: 0, notes: ''
    });
    setOpenForm(true);
  };

  const openEdit = (p: Prospect) => {
    setEditing(p);
    setForm({
      name: p.name, legalName: p.legalName || '', buyerName: p.buyerName || '',
      generalStatus: p.generalStatus || 'Activo', followUpStatus: p.followUpStatus || '',
      nextStep: p.nextStep || '', phone: p.phone || '', email: p.email || '',
      city: p.city || '', websiteUrl: p.websiteUrl || '', sector: p.sector || '',
      assignedSeller: p.assignedSeller || '', branchCount: p.branchCount || 0,
      notes: p.notes || ''
    });
    setOpenForm(true);
  };

  const save = async () => {
    if (!form.name) return;
    try {
      if (editing) {
        await api.updateProspect(editing.id, form);
      } else {
        await api.createProspect(form);
      }
      setOpenForm(false);
      setEditing(null);
      await load();
    } catch (e: any) {
      setError(e?.response?.data?.message || 'Error al guardar');
    }
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('¿Eliminar este prospecto?')) return;
    try {
      await api.deleteProspect(id);
      await load();
    } catch (e: any) {
      setError(e?.response?.data?.message || 'Error al eliminar');
    }
  };

  const handleConvert = async (p: Prospect) => {
    if (!window.confirm(`¿Convertir "${p.name}" a cliente?`)) return;
    try {
      const result = await api.convertProspectToCustomer(p.id);
      alert(`Cliente creado: ${result.customerName}`);
      await load();
    } catch (e: any) {
      setError(e?.response?.data?.message || 'Error al convertir');
    }
  };

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} justifyContent="space-between" alignItems={{ md: 'center' }} sx={{ mb: 2 }}>
        <Typography variant="h4" fontWeight={700}>Prospectos</Typography>
        <Stack direction="row" spacing={1}>
          <Button startIcon={<Upload />} variant="outlined" onClick={() => navigate('/prospects/import')}>Importar</Button>
          <Button startIcon={<Add />} variant="contained" onClick={openNew}>Nuevo Prospecto</Button>
        </Stack>
      </Stack>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

      {/* Filters */}
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={1} sx={{ mb: 2 }}>
        <TextField size="small" label="Buscar" value={search} onChange={e => setSearch(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && handleSearch()} sx={{ minWidth: 200 }} />
        <TextField size="small" select label="Estado" value={statusFilter} onChange={e => setStatusFilter(e.target.value)} sx={{ minWidth: 150 }}>
          <MenuItem value="">Todos</MenuItem>
          {STATUS_OPTIONS.map(s => <MenuItem key={s} value={s}>{s}</MenuItem>)}
        </TextField>
        <TextField size="small" label="Vendedor" value={sellerFilter} onChange={e => setSellerFilter(e.target.value)} sx={{ minWidth: 150 }} />
        <Button variant="outlined" onClick={handleSearch}>Filtrar</Button>
      </Stack>

      {/* Table */}
      <Card>
        <CardContent sx={{ p: 0 }}>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>N°</TableCell>
                  <TableCell>Nombre</TableCell>
                  <TableCell>Razón Social</TableCell>
                  <TableCell>Comprador</TableCell>
                  <TableCell>Estado</TableCell>
                  <TableCell>Seguimiento</TableCell>
                  <TableCell>Next Step</TableCell>
                  <TableCell>Teléfono</TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell>Ciudad</TableCell>
                  <TableCell>Sector</TableCell>
                  <TableCell>Vendedor</TableCell>
                  <TableCell>Sucursales</TableCell>
                  <TableCell>Acciones</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {items.map(p => (
                  <TableRow key={p.id} hover>
                    <TableCell>{p.prospectNumber || p.id}</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>{p.name}</TableCell>
                    <TableCell>{p.legalName || '—'}</TableCell>
                    <TableCell>{p.buyerName || '—'}</TableCell>
                    <TableCell>
                      <Chip size="small" label={p.generalStatus || 'Sin estado'}
                        sx={{ bgcolor: STATUS_COLORS[p.generalStatus || ''] || '#9E9E9E', color: '#fff' }} />
                    </TableCell>
                    <TableCell>{p.followUpStatus || '—'}</TableCell>
                    <TableCell>
                      <Tooltip title={p.nextStep || ''}>
                        <Typography variant="body2" noWrap sx={{ maxWidth: 150 }}>{p.nextStep || '—'}</Typography>
                      </Tooltip>
                    </TableCell>
                    <TableCell>{p.phone || '—'}</TableCell>
                    <TableCell>{p.email || '—'}</TableCell>
                    <TableCell>{p.city || '—'}</TableCell>
                    <TableCell>{p.sector || '—'}</TableCell>
                    <TableCell>{p.assignedSeller || '—'}</TableCell>
                    <TableCell>{p.branchCount || 0}</TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={0.5}>
                        <Tooltip title="Editar"><IconButton size="small" onClick={() => openEdit(p)}><Edit fontSize="small" /></IconButton></Tooltip>
                        {!p.isConverted && (
                          <Tooltip title="Convertir a Cliente"><IconButton size="small" color="success" onClick={() => handleConvert(p)}><PersonAdd fontSize="small" /></IconButton></Tooltip>
                        )}
                        <Tooltip title="Eliminar"><IconButton size="small" color="error" onClick={() => handleDelete(p.id)}><Delete fontSize="small" /></IconButton></Tooltip>
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))}
                {items.length === 0 && !loading && (
                  <TableRow><TableCell colSpan={14}><Typography variant="body2" color="text.secondary" align="center">Sin prospectos</Typography></TableCell></TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
          {totalPages > 1 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
              <Pagination count={totalPages} page={page} onChange={(_, p) => { setPage(p); load(p); }} />
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Create/Edit Dialog */}
      <Dialog open={openForm} onClose={() => setOpenForm(false)} maxWidth="md" fullWidth>
        <DialogTitle>{editing ? 'Editar Prospecto' : 'Nuevo Prospecto'}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField label="Nombre" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} fullWidth required />
              <TextField label="Razón Social" value={form.legalName} onChange={e => setForm({ ...form, legalName: e.target.value })} fullWidth />
            </Stack>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField label="Comprador" value={form.buyerName} onChange={e => setForm({ ...form, buyerName: e.target.value })} fullWidth />
              <TextField select label="Estado" value={form.generalStatus} onChange={e => setForm({ ...form, generalStatus: e.target.value })} fullWidth>
                {STATUS_OPTIONS.map(s => <MenuItem key={s} value={s}>{s}</MenuItem>)}
              </TextField>
              <TextField select label="Seguimiento" value={form.followUpStatus} onChange={e => setForm({ ...form, followUpStatus: e.target.value })} fullWidth>
                <MenuItem value="">Sin estado</MenuItem>
                {FOLLOWUP_OPTIONS.map(s => <MenuItem key={s} value={s}>{s}</MenuItem>)}
              </TextField>
            </Stack>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField label="Teléfono" value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} fullWidth />
              <TextField label="Email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} fullWidth />
              <TextField label="Ciudad" value={form.city} onChange={e => setForm({ ...form, city: e.target.value })} fullWidth />
            </Stack>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField label="Web / IG" value={form.websiteUrl} onChange={e => setForm({ ...form, websiteUrl: e.target.value })} fullWidth />
              <TextField label="Sector" value={form.sector} onChange={e => setForm({ ...form, sector: e.target.value })} fullWidth />
              <TextField label="Vendedor Asignado" value={form.assignedSeller} onChange={e => setForm({ ...form, assignedSeller: e.target.value })} fullWidth />
            </Stack>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField label="Sucursales" type="number" value={form.branchCount} onChange={e => setForm({ ...form, branchCount: Number(e.target.value) })} sx={{ width: 120 }} />
              <TextField label="Next Step" value={form.nextStep} onChange={e => setForm({ ...form, nextStep: e.target.value })} fullWidth />
            </Stack>
            <TextField label="Notas" multiline minRows={2} value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenForm(false)}>Cancelar</Button>
          <Button variant="contained" onClick={save}>Guardar</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ProspectsPage;
